import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import '../Main.scss';
import './BreadCrumbs.scss';

import {
    Tooltip
} from '@mui/material';

import {
    Button
} from 'reactstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faInfo,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { faAngleRight } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

export type BreadCrumb = {
    content: string;
    link?: string;
    navCount?: number;
}

type Props = {
    containerStyling?: string;
    crumbs: BreadCrumb[];
    /**
     * 'title' renders the crumbs as a page title bar: every crumb but the last is a floating button,
     * the last is the page's h1, and `trailing` (e.g. a page actions menu) follows it.
     */
    variant?: 'title';
    trailing?: React.ReactNode;
    /** Accessible label of the title variant's nav landmark; supply it from the portal text (English when absent). */
    navLabel?: string;
    showInformation?: {
        active: boolean;
        tipTitle?: string;
        cancelTip: boolean;
        onInfoClick: (idTarget: string) => void;
    }
}

export const BreadCrumbs: React.FC<Props> = (props): React.ReactElement => {
    const {
        containerStyling,
        crumbs,
        showInformation,
        variant,
        trailing,
        navLabel,
    } = props;

    const infoIconId = 'bread-crumbs-info-icon';

    const navigate = useNavigate();

    const [contentEmpty, setContentEmpty] = React.useState<boolean>(false);

    const InfoIconContent = React.useMemo(() => {
        if (!showInformation) {
            return (<></>);
        }
        return (
            <Button
                id={infoIconId}
                onClick={() => showInformation.onInfoClick(infoIconId)}
                color="secondary"
            >
                <FontAwesomeIcon icon={faInfo} />
            </Button>
        );

        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showInformation]);

    React.useEffect(() => {
        let empty = true;

        crumbs.forEach(crumb => {
            if (!!crumb.content) {
                empty = false;
            }
        });
        setContentEmpty(empty);
    }, [crumbs])

    const executeLink = (crumb: BreadCrumb): void => {
        if (!!crumb.link) {
            crumb.navCount !== undefined ? navigate(crumb!.navCount) : navigate(crumb.link || '')        
        }
    }

    if (contentEmpty) {
        return (<></>);
    } else if (variant === 'title') {
        const lastIndex = crumbs.length - 1;
        // The last crumb is the page title and is often not known yet (e.g. a date that loads with the page): while it
        // is empty, show the crumbs before it as they are, with no empty heading and no dangling separator.
        const titlePending = lastIndex >= 0 && !crumbs[lastIndex].content;
        return (
            <nav className={'page-alt-breadcrumb'} aria-label={navLabel || 'Breadcrumb'}>
                {
                    crumbs.map((crumb, index) => {
                        const rootClass = index === 0 && lastIndex > 1 ? ' page-alt-breadcrumb__root' : '';
                        return (
                            <React.Fragment key={`breadcrumb${index}`}>
                                {index === lastIndex ? (
                                    titlePending ? null : <h1 className={'page-alt-breadcrumb__current'}>{crumb.content}</h1>
                                ) : (
                                    crumb.link ? (
                                        <button
                                            type={'button'}
                                            className={`cr-btn cr-btn--floating page-alt-breadcrumb__link${index === lastIndex - 1 ? ' page-alt-breadcrumb__link--name' : ''}${rootClass}`}
                                            title={crumb.content}
                                            onClick={() => executeLink(crumb)}
                                        >
                                            <span className={'cr-btn__label'}>{crumb.content}</span>
                                        </button>
                                    ) : (
                                        // Nothing to navigate to: plain text with the same box as a link, not a button that does nothing.
                                        <span
                                            className={`cr-btn cr-btn--floating page-alt-breadcrumb__link page-alt-breadcrumb__link--static${index === lastIndex - 1 ? ' page-alt-breadcrumb__link--name' : ''}${rootClass}`}
                                            title={crumb.content}
                                        >
                                            <span className={'cr-btn__label'}>{crumb.content}</span>
                                        </span>
                                    )
                                )}
                                {index < lastIndex && !(titlePending && index === lastIndex - 1) && (
                                    <FontAwesomeIcon
                                        icon={faAngleRight}
                                        className={`page-alt-breadcrumb__sep${rootClass}`}
                                        aria-hidden={true}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })
                }
                {trailing}
            </nav>
        );
    } else {
        return (
            <div className={`crumb-container ${containerStyling}`}>
                {
                    crumbs.map((crumb, index) => {
                        return (index !== 0) ?
                            (
                                <div key={`breadcrumb${index}`}>
                                    &nbsp;&gt;&nbsp;
                                    <span
                                        className={!!crumb.link ? 'link' : 'disabled-link'}
                                        onClick={() => executeLink(crumb)}
                                    >
                                        {crumb.content}
                                    </span>
                                </div>
                            ) :
                            (
                                <div key={`breadcrumb${index}`}>
                                    <span
                                        className={!!crumb.link ? 'link' : 'disabled-link'}
                                        onClick={() => executeLink(crumb)}
                                    >
                                        {crumb.content}
                                    </span>
                                </div>
                            )
                    })
                }
                {
                    !!showInformation && showInformation.active && !!showInformation.tipTitle && (
                        <Tooltip title={showInformation.cancelTip ? '' : showInformation!.tipTitle}>
                            {InfoIconContent}
                        </Tooltip>
                    )
                }
                {
                    !!showInformation && showInformation.active && !showInformation.tipTitle && (
                        <>
                            {InfoIconContent}
                        </>
                    )
                }
            </div>
        );
    }
}
